import { Phone, MessageSquare, Mic, Database, PhoneForwarded } from "lucide-react";

/**
 * Premium coded "business phone system" mockup: a dark phone-console panel
 * showing a live recorded call, a text thread on the same number, and the call
 * logging itself to the CRM. Hero visual for the /platform/business-phone-system
 * spoke. Illustrative sample data only (like the other capability mockups), no
 * claimed client results.
 */
export const PhoneSystemMockup = () => {
  return (
    <div className="relative w-full max-w-[420px] mx-auto select-none">
      {/* floating badge */}
      <div className="absolute -top-3 right-4 z-10 rounded-full bg-gradient-to-br from-sky-600 to-blue-600 px-3.5 py-1.5 text-xs font-bold text-white shadow-lg shadow-sky-900/30">
        Lines from $2/mo
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900/80 shadow-2xl backdrop-blur-sm">
        {/* console header */}
        <div className="border-b border-slate-700 px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-sky-500/15 text-sky-400">
                <Phone className="h-4.5 w-4.5" />
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-bold text-white">Your business line</p>
                <p className="mt-0.5 text-xs text-slate-400">Calls, texts &amp; AI on one number</p>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-slate-800 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-300">
              3 lines
            </span>
          </div>
        </div>

        {/* live call strip */}
        <div className="flex items-center justify-between gap-2 border-b border-slate-800 bg-sky-500/5 px-5 py-3">
          <div className="flex items-center gap-2 text-sky-300">
            <PhoneForwarded className="h-4 w-4" />
            <span className="text-xs font-semibold">Line 1: live call · 02:41</span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 text-[10px] font-bold text-rose-300">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-rose-400" />
            REC
          </span>
        </div>

        <div className="space-y-3 px-5 py-4">
          {/* text thread on the same number */}
          <div className="flex items-center gap-3">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-700 text-slate-300">
              <MessageSquare className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-slate-200">Text on the same number</span>
              <p className="mt-0.5 truncate text-[11px] text-slate-500">
                "Got your voicemail. Does 2pm tomorrow work?"
              </p>
            </div>
            <span className="h-2 w-2 shrink-0 rounded-full bg-sky-500" />
          </div>

          {/* AI answered after hours */}
          <div className="flex items-center gap-3">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-slate-700 text-slate-300">
              <Mic className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-slate-200">AI answered at 9:12 PM</span>
              <p className="mt-0.5 truncate text-[11px] text-slate-500">
                Booked an estimate while the shop was closed
              </p>
            </div>
            <span className="h-2 w-2 shrink-0 rounded-full bg-slate-600" />
          </div>

          {/* logged to CRM row */}
          <div className="flex items-center gap-3 rounded-xl border border-sky-500/30 bg-sky-500/10 p-3">
            <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-sky-500 to-blue-500 text-white">
              <Database className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <span className="text-xs font-bold text-white">Logged to the CRM</span>
              <p className="mt-0.5 text-[11px] leading-snug text-sky-200/80">
                Jordan P.: recording, transcript &amp; follow-up task attached
              </p>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-3 px-4 text-center text-xs text-slate-400">
        Every call recorded, every text kept, every conversation in your CRM. Not lost on a handset.
      </p>
    </div>
  );
};

export default PhoneSystemMockup;
